import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import {
  ColumnApi,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent
} from 'ag-grid-community';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LocaleEnum } from 'src/app/submit-lecture/lecture.values';
import { resizeObservable } from 'src/app/utils/dom.utils';
import { LocalStorageService } from 'src/app/core/local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/core/local-storage/local-storage.enum';
const engligh = require('src/assets/i18n/en.json');
const hebrew = require('src/assets/i18n/he.json');
interface GridState {
  filter: Record<string, any>;
  state: ColumnState[];
}

interface FilterStatus {
  name: string;
  value: string;
}

const dateFiltersKeysMap = new Map(
  [['scheduled_date', 'RESOURCE_LIST.LECTURES_TABLE.SCHEDULED_DATE'],
    ['assigned_at', 'RESOURCE_LIST.LECTURES_TABLE.ASSIGNED_AT'],
    ['created_at', 'RESOURCE_LIST.LECTURES_TABLE.CREATED_DATE'],
    ['lastWeek', 'RESOURCE_LIST.LECTURES_TABLE.LAST_WEEK'],
    ['lastMonth', 'RESOURCE_LIST.LECTURES_TABLE.LAST_MONTH'],
    ['last3Monthes', 'RESOURCE_LIST.LECTURES_TABLE.LAST_3_MONTH']
  ]
);

const AG_GRID_LOCALE_EN = engligh.AG_GRID_LOCALE_EN;
const AG_GRID_LOCALE_HE = hebrew.AG_GRID_LOCALE_HE;

@Component({
  selector: 'ag-grid-table',
  templateUrl: './ag-grid-table.component.html',
  styleUrls: ['./ag-grid-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AgGridTableComponent implements OnInit, OnDestroy, OnChanges {
  constructor(private readonly translate: TranslateService,
              private localStorageService: LocalStorageService) {
  }
  private unsubscribe$ = new Subject();

  @ViewChild('agGrid', { read: ElementRef })
  private gridElement: ElementRef<HTMLElement>;

  private gridApi: GridApi;
  private columnApi: ColumnApi;
  private subscription;
  gridActivated = true;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  dateFiltersArr: FilterStatus[] = [];

  @Input() localStorageKey: LocalStorageKeys;
  @Input() rows: unknown[];
  @Input() enableRtl: boolean;
  @Input() localeText: unknown;
  @Input() columnDefs: unknown;
  @Input() isExternalFilterPresent: unknown;
  @Input() doesExternalFilterPass: unknown;
  @Input() gridOptions: GridOptions;
  @Input() externalFilterValue: unknown;

  @Output() onGridReady: EventEmitter<GridReadyEvent> = new EventEmitter<GridReadyEvent>();
  @Output() onRowClickedEvent: EventEmitter<RowClickedEvent> = new EventEmitter<RowClickedEvent>();
  @Output() onSelectionChangedEvent: EventEmitter<unknown[]> = new EventEmitter<unknown[]>();

  ngOnInit(): void{
    if (this.onRowClickedEvent.observers.length > 0) {
      this.gridOptions.rowClass = 'clickable';
    }
    this.localeText = this.translate.currentLang === LocaleEnum.HE ? AG_GRID_LOCALE_HE : AG_GRID_LOCALE_EN;
    this.onLangChange(this.translate.currentLang);

    this.subscription = this.translate.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.onLangChange(event.lang);
    });
  }

  ngOnChanges(changes): void {
    if (changes?.enableRtl?.currentValue !== changes?.enableRtl?.oldValue) {
      this.gridActivated = false;
      this.gridOptions.enableRtl = changes?.enableRtl?.currentValue;
      this.localeText = this.translate.currentLang === LocaleEnum.HE ? AG_GRID_LOCALE_HE : AG_GRID_LOCALE_EN;
      setTimeout(() => (this.gridActivated = true));
    }
    if (changes?.externalFilterValue?.currentValue !== changes?.enableRtl?.previousValue) {
      this.dateFiltersArr = [];
    }
  }

  onRowClicked(event: RowClickedEvent): void{
    this.onRowClickedEvent.emit(event);
  }

  onSelectionChanged(): void {
    const selectedRows = this.gridApi.getSelectedRows();
    this.onSelectionChangedEvent.emit(selectedRows);
  }

  onGridReadyInner(params: GridReadyEvent): void {
    this.gridApi = params?.api;
    this.columnApi = params?.columnApi;
    this.addResizeGridListener();
    this.onGridReady.emit(params);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subscription.unsubscribe();
  }

  onLangChange(lang: string): void {
    let text = 'No Results Found';
    switch (lang) {
      case LocaleEnum.HE:
        text = 'אין תוצאות';
        break;
      default:
        break;
    }
    this.overlayLoadingTemplate =
    '<div class="loadingx" style="margin: 7em"></div> <span class="ag-overlay-loading-center " style="font-size: 18px; z-index: 100000"> Loading Rows ...</span>';
    this.overlayNoRowsTemplate =
    `<span class="ag-overlay-loading-center loadingNoRows"><div><img src="/assets/images/no-results.svg"></img></div><div>${text}</div></span>`;
  }

  getAllFilteredRows(): void {
    // TODO
    this.gridApi.selectAllFiltered();
    this.onSelectionChangedEvent.emit([]);
  }

  private addResizeGridListener = () => {
    resizeObservable(this.gridElement.nativeElement)
      .pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => this.gridOptions?.api?.sizeColumnsToFit());
  }

  populateDateFiltersArr(filter: Record<string, any>): void {
    this.dateFiltersArr = [];
    for (const prop in filter) {
      if (filter[prop].filterType === 'date') {
        if ((filter[prop].type === 'lastWeek') || (filter[prop].type === 'lastMonth') || (filter[prop].type === 'last3Monthes')) {
          this.dateFiltersArr.push({
            name: this.translate.instant(dateFiltersKeysMap.get(prop)),
            value: this.translate.instant(dateFiltersKeysMap.get(filter[prop].type))}
            );
        } else if (filter[prop].type === 'specificDate') {
          this.dateFiltersArr.push({
            name: this.translate.instant(dateFiltersKeysMap.get(prop)),
            value: filter[prop].dateFrom.split(' ')[0]});
        }
        else if (filter[prop].type === 'inRange') {
          this.dateFiltersArr.push({
            name: this.translate.instant(dateFiltersKeysMap.get(prop)),
            value: filter[prop].dateFrom.split(' ')[0]
              + this.translate.instant('RESOURCE_LIST.LECTURES_TABLE.TO_DATE')
              + filter[prop].dateTo.split(' ')[0]});
        }
        else {
          console.log('Invalid filter date type !!');
        }
      }
    }
  }

  onStateChanged(): void {
    const filter: Record<string, any> = this.gridApi.getFilterModel();
    this.populateDateFiltersArr(filter);
    const state: ColumnState[] = this.columnApi.getColumnState();
    const gridState: GridState = {
      filter,
      state
    };
    this.localStorageService.write<GridState>(this.localStorageKey, gridState);
  }

  getFromStorage(storageKey: string): GridState | undefined {
    const itemJson: string = localStorage.getItem(storageKey);
    if (itemJson) {
      return JSON.parse(itemJson);
    } else {
      return undefined;
    }
  }

  private setStateFromStorage(): void {
    const gridState: GridState | undefined = this.localStorageService.read<GridState>(this.localStorageKey);
    if (gridState) {
      const { filter, state } = gridState;
      this.gridApi.setFilterModel(filter);
      this.columnApi.setColumnState(state);
      this.populateDateFiltersArr(filter);
    }
  }

  onSortChanged(): void {
    // needed for addGlobalListener
  }

  onFilterChanged(): void {
    // needed for addGlobalListener
  }

  onFirstDataRendered(): void {
    this.setStateFromStorage();
    this.gridApi.addGlobalListener((type: string): void => {
      this.onStateChanged();
    });
  }
}
